import axiosInstance from '@/api/axiosInstance';
import { adoptSignInTenant } from '@/api/tokenManager';
import { API_PREFIX } from '@/api/endpoints/api.constants';
import { TokenResponse } from '@/types/authentication.types';

export type MfaMethodType = 'authenticator' | 'passkey' | 'email_token';
export type MfaTargetMode = 'all' | 'cohorts';

export interface MfaCohortRef {
  id: number;
  name: string;
  members?: number;
}

export interface MfaPolicy {
  id?: number;
  enabled: boolean;
  allow_authenticator: boolean;
  allow_passkey: boolean;
  allow_email_token: boolean;
  target_mode: MfaTargetMode;
  cohort_ids: number[];
  cohorts?: MfaCohortRef[];
}

export interface MfaEnrolledMethod {
  id: number;
  method: MfaMethodType;
  label?: string;
  created_at?: string;
  last_used_at?: string;
}

export interface TotpStart {
  secret: string;
  otpauth_uri: string;
  qr: string; // data:image/png;base64,...
}

export interface MfaLoginContext {
  allowed_methods: MfaMethodType[];
  enrolled_methods: MfaMethodType[];
  must_enrol: boolean;
  email_hint?: string;
}

/** Login response is either a normal token pair or an MFA challenge. */
export interface MfaRequiredResponse extends MfaLoginContext {
  mfa_required: true;
  mfa_token: string;
}

const POLICY = `${API_PREFIX}/mfa-policy`;
const METHODS = `${API_PREFIX}/mfa`;
const CHALLENGE = `${API_PREFIX}/auth/mfa`;

/** Admin: tenant MFA policy. */
export const MfaPolicyApi = {
  get: async (): Promise<MfaPolicy> =>
    (await axiosInstance.get<MfaPolicy>(`${POLICY}/`)).data,
  save: async (data: MfaPolicy): Promise<MfaPolicy> =>
    (await axiosInstance.put<MfaPolicy>(`${POLICY}/`, data)).data,
  searchCohorts: async (q?: string): Promise<MfaCohortRef[]> =>
    (await axiosInstance.get<MfaCohortRef[]>(`${POLICY}/cohorts`, { params: q ? { q } : undefined })).data,
};

/** Account settings: the current user's enrolled second factors. */
export const MfaMethodsApi = {
  list: async (): Promise<MfaEnrolledMethod[]> =>
    (await axiosInstance.get<MfaEnrolledMethod[]>(`${METHODS}/methods`)).data,
  remove: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${METHODS}/methods/${id}`);
  },
  totpStart: async (): Promise<TotpStart> =>
    (await axiosInstance.post<TotpStart>(`${METHODS}/methods/totp/start`)).data,
  totpActivate: async (code: string): Promise<void> => {
    await axiosInstance.post(`${METHODS}/methods/totp/activate`, { code });
  },
  passkeyRegisterOptions: async (): Promise<any> =>
    (await axiosInstance.post(`${METHODS}/methods/passkey/register-options`)).data,
  passkeyRegisterVerify: async (credential: any, label?: string): Promise<void> => {
    await axiosInstance.post(`${METHODS}/methods/passkey/register-verify`, { credential, label });
  },
};

/** A passed challenge signs the user in: remember the tenant the tokens were issued for (UAT DEF-233). */
const signedIn = (res: TokenResponse): TokenResponse => {
  adoptSignInTenant(res?.access_token);
  return res;
};

/** Login-time MFA challenge (consumes the mfa_pending token). */
export const MfaChallengeApi = {
  context: async (mfa_token: string): Promise<MfaLoginContext> =>
    (await axiosInstance.post<MfaLoginContext>(`${CHALLENGE}/context`, { mfa_token })).data,

  emailSend: async (mfa_token: string): Promise<{ sent: boolean; email_hint?: string }> =>
    (await axiosInstance.post(`${CHALLENGE}/email/send`, { mfa_token })).data,
  emailVerify: async (mfa_token: string, code: string): Promise<TokenResponse> =>
    signedIn((await axiosInstance.post<TokenResponse>(`${CHALLENGE}/email/verify`, { mfa_token, code })).data),

  totpVerify: async (mfa_token: string, code: string): Promise<TokenResponse> =>
    signedIn((await axiosInstance.post<TokenResponse>(`${CHALLENGE}/totp/verify`, { mfa_token, code })).data),

  passkeyOptions: async (mfa_token: string): Promise<any> =>
    (await axiosInstance.post(`${CHALLENGE}/passkey/options`, { mfa_token })).data,
  passkeyVerify: async (mfa_token: string, credential: any): Promise<TokenResponse> =>
    signedIn((await axiosInstance.post<TokenResponse>(`${CHALLENGE}/passkey/verify`, { mfa_token, credential })).data),

  // In-login enrolment (forced MFA, nothing set up yet)
  enrollTotpStart: async (mfa_token: string): Promise<TotpStart> =>
    (await axiosInstance.post<TotpStart>(`${CHALLENGE}/enroll/totp/start`, { mfa_token })).data,
  enrollTotpVerify: async (mfa_token: string, code: string): Promise<TokenResponse> =>
    signedIn((await axiosInstance.post<TokenResponse>(`${CHALLENGE}/enroll/totp/verify`, { mfa_token, code })).data),
  enrollPasskeyOptions: async (mfa_token: string): Promise<any> =>
    (await axiosInstance.post(`${CHALLENGE}/enroll/passkey/options`, { mfa_token })).data,
  enrollPasskeyVerify: async (mfa_token: string, credential: any): Promise<TokenResponse> =>
    signedIn((await axiosInstance.post<TokenResponse>(`${CHALLENGE}/enroll/passkey/verify`, { mfa_token, credential })).data),
};
